import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import {
    ExclamationCircle,
    Exclamation,
    Check,
    Clock,
    LockFill,
    X,
} from 'react-bootstrap-icons';

// CSS
import "./style.css";

import eye_show from '../../assets/img/icon/eye_show.png';
import eye_hide from '../../assets/img/icon/eye_hide.png';

const COOLDOWN_MAX_SECONDS = 10;

// Rate Limit Modal

const pad = (n) => String(n).padStart(2, '0');

export const formatMMSS = (s) =>
    `${pad(Math.floor(s / 60))}:${pad(s % 60)}`;

export const formatHHMMSS = (s) =>
    `${pad(Math.floor(s / 3600))}:${pad(Math.floor((s % 3600) / 60))}:${pad(s % 60)}`;

function useCountdown(endAt, onFinish) {
    const calc = () => Math.max(0, Math.ceil((endAt - Date.now()) / 1000));
    const [left, setLeft] = useState(calc);

    const finishRef = useRef(onFinish);
    finishRef.current = onFinish;

    useEffect(() => {
        const timer = setInterval(() => {
            const value = calc();
            setLeft(value);

            if (value <= 0) {
                clearInterval(timer);
                if (finishRef.current) finishRef.current();
            }
        }, 250);

        return () => clearInterval(timer);
    }, [endAt]);

    return left;
}

function WarningContent({ onClose }) {
    return (
        <>
            <div className="rl-icon rl-icon--orange">
                <Exclamation size={44} />
            </div>
            <h5 className="rl-title">Terlalu banyak percobaan login</h5>
            <p className="rl-text">
                Username atau password salah beberapa kali. Jika percobaan
                berikutnya gagal, akun Anda akan masuk ke mode cooldown.
            </p>
            <button type="button" className="rl-btn" onClick={onClose}>
                Tutup
            </button>
        </>
    );
}

function CooldownContent({ endAt, onFinish }) {
    const left = useCountdown(endAt, onFinish);

    return (
        <>
            <div className="rl-icon rl-icon--blue">
                <Clock size={30} />
            </div>
            <h5 className="rl-title">Terlalu banyak percobaan login</h5>
            <p className="rl-text">Silakan tunggu beberapa detik.</p>
            <div className="rl-timer rl-timer--blue">
                <Clock size={34} />
                <div className="rl-timer-body">
                    <span className="rl-timer-label">Cooldown</span>
                    <span className="rl-timer-value">{formatMMSS(left)}</span>
                </div>
            </div>
        </>
    );
}

function DoneContent({ onClose }) {
    return (
        <>
            <div className="rl-icon rl-icon--green">
                <Check size={44} />
            </div>
            <h5 className="rl-title">Silakan login kembali</h5>
            <p className="rl-text">
                Waktu cooldown telah selesai. <br />
                Anda dapat mencoba login kembali.
            </p>
            <button type="button" className="rl-btn" onClick={onClose}>
                Tutup
            </button>
        </>
    );
}

function BlockContent({ endAt, onClose, onFinish }) {
    const left = useCountdown(endAt, onFinish);

    return (
        <>
            <div className="rl-icon rl-icon--red">
                <LockFill size={26} />
            </div>
            <h5 className="rl-title">Akun Anda diblokir</h5>
            <p className="rl-text">
                Terlalu banyak percobaan login. <br />
                Akun Anda telah diblokir sementara untuk keamanan.
            </p>
            <div className="rl-timer rl-timer--red">
                <Clock size={28} />
                <div className="rl-timer-body">
                    <span className="rl-timer-label">Waktu blokir</span>
                    <span className="rl-timer-value">{formatHHMMSS(left)}</span>
                </div>
            </div>
            <button type="button" className="rl-btn" onClick={onClose}>
                Tutup
            </button>
        </>
    );
}

function RateLimitModal({ type, endAt, onClose, onFinished }) {
    if (!type) return null;

    return createPortal(
        <div className="rl-overlay" onClick={onClose}>
            <div
                className="rl-card"
                role="dialog"
                aria-modal="true"
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    type="button"
                    className="rl-close"
                    onClick={onClose}
                    aria-label="Tutup"
                >
                    <X size={28} />
                </button>

                {type === 'warning' && <WarningContent onClose={onClose} />}

                {type === 'cooldown' && (
                    <CooldownContent
                        endAt={endAt}
                        onFinish={() => onFinished && onFinished('cooldown')}
                    />
                )}

                {type === 'done' && <DoneContent onClose={onClose} />}

                {type === 'block' && (
                    <BlockContent
                        endAt={endAt}
                        onClose={onClose}
                        onFinish={() => onFinished && onFinished('block')}
                    />
                )}
            </div>
        </div>,
        document.body
    );
}

// Login
function Login(props) {
    const [keepLogin, setKeepLogin] = useState(false);
    const [email, setEmail] = useState('');
    const [validationEmail, setValidationEmail] = useState();
    const [password, setPassword] = useState('');
    const [validationPassword, setValidationPassword] = useState();
    const [error, setError] = useState();
    const [loading, setLoading] = useState();

    const [modal, setModal] = useState(null);
    const [lock, setLock] = useState(null);

    const handlerSetKeepLogin = () => setKeepLogin(!keepLogin);

    const [passwordShown, setPasswordShown] = useState(false);
    const togglePasswordVisiblity = () => {
        setPasswordShown(passwordShown ? false : true);
    };

    const showRateLimit = (result) => {
        const seconds = Math.max(parseInt(result.retry_after, 10) || 1, 1);

        const type = result.type
            ? result.type
            : (seconds > COOLDOWN_MAX_SECONDS ? 'block' : 'cooldown');

        setLock({ type, endAt: Date.now() + seconds * 1000 });
        setModal(type);
    };

    const handleModalFinished = (type) => {
        setLock(null);
        setModal(type === 'cooldown' ? 'done' : null);
    };

    const doValidation = () => {
        setValidationEmail(false)
        setValidationPassword(false)
        if (email === '' && password === '') {
            setValidationEmail(true)
            setValidationPassword(true)
            setEmail('');
            setPassword('');
        } else if (email === '') {
            setValidationEmail(true)
            setEmail('');
        } else if (password === '') {
            setValidationPassword(true)
            setPassword('');
        } else {
            doLogin()
        }

    }
    const doLogin = () => {
        let data = { email, password, keepLogin };
        setLoading(true);
        setError(null);
        setValidationEmail(false)
        setValidationPassword(false)

        fetch("/_api/main/login", {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(data),

        })
            .then(res => res.json())
            .then(
                (result) => {

                    if (result.success) {
                        window.location.href = result.redirect_uri;
                        return;
                    }

                    if (result.rate_limited) {
                        showRateLimit(result);
                        setLoading(false);
                        return;
                    }

                    if (result.error) {
                        setError(result.error.code);

                        if (result.error.warning) {
                            setModal('warning');
                        }
                    }

                    setLoading(false)

                }
            )
            .catch(error => {
                setError(true)
                setLoading(false)
            }
            )
    }

    return (

            <div className="main-container" data-page={props.appName} data-aos={"fade-up"}>
                <div className="form-title mb-3">
                    <div className="mb-0">
                        <h5>Masuk</h5>
                    </div>
                    <div>
                        <p>Silahkan masuk menggunakan akun kamu</p>
                    </div>
                </div>

                <div className="main-form">
                    <form>
                        <div className="mb-2">
                            <label htmlFor="email">Email</label>
                            <input value={email} onChange={(e) => setEmail(e.target.value)} type="text" name="email" id="email" placeholder="Masukkan email / username" className={ error === 40001 ? "form-control px-3 is-invalid" : "form-control px-3" }  required />
                            {
                                validationEmail ?
                                    <div className="invalid-feedback d-block">
                                        <ExclamationCircle /> Email / username tidak boleh kosong
                                    </div>
                                    : null
                            }
                            {
                                error === 40001 ?
                                    <div className="invalid-feedback d-block">
                                        <ExclamationCircle size='14' /> Email tidak ditemukan
                                    </div>
                                    : null
                            }
                        </div>
                        <div className="mb-3">
                            <label htmlFor="password">Password</label>
                            <div className="d-flex input-password">
                            <input value={password} onChange={(e) => setPassword(e.target.value)} type={passwordShown ? "text" : "password"} name="password" id="password" placeholder="Masukkan password" className={ error === 40002 ? "form-control px-3 is-invalid" : "form-control px-3"} required />
                                <div className="input-group-append form-control-feedback">
                                    <div className="input-group-text toggle-password">
                                    <img onClick={togglePasswordVisiblity} className="icon-btn" alt="eye" src={passwordShown ? eye_hide : eye_show} style={{ width: '25px', height: '25px' }} />
                                    </div>
                                </div>
                            </div>
                            {
                                validationPassword ?
                                    <div className="invalid-feedback d-block">
                                        <ExclamationCircle /> Password tidak boleh kosong
                                    </div>
                                    : null
                            }
                            {
                                error === 40002 ?
                                    <div className="invalid-feedback d-block">
                                        <ExclamationCircle size='14' /> Password tidak sesuai
                                    </div>
                                    : null
                            }
                        </div>

                        <div className="mb-4 d-flex">
                            <div className="col-6 form-check">
                                <input onChange={handlerSetKeepLogin} type="checkbox" name="keepLogin" id="keepLogin" className="form-check-input" />
                                <label htmlFor="keepLogin" className="foem-check-login">Ingat saya</label>
                            </div>
                            <div className="col-6 forgot-password">
                                <Link className="link" to="/forgot">Lupa password?</Link>
                            </div>
                        </div>
                        
                        <button type="submit" className="form-control mb-2" disabled={loading} onClick={(e) => { e.preventDefault(); doValidation() }}>
                            {
                                loading ?
                                    <div className="loading-container">
                                        <span className="spinner-border text-light loading" role="status"></span> Tunggu sebentar
                                    </div>
                                    : "Masuk"
                            }
                        </button>

                        <div className="col-12 text-center">
                            <span data-testid="not-have-account">Belum punya akun?</span> <br />
                            <span data-testid="not-have-account">Silahkan hubungi sekolah</span>
                        </div>
                    </form>
                </div>

                <RateLimitModal
                    type={modal}
                    endAt={lock ? lock.endAt : null}
                    onClose={() => setModal(null)}
                    onFinished={handleModalFinished}
                />

            </div>
    );
}

export default Login;